import React, { useEffect, useState } from 'react';
import { IntegrationsStep, integrationIntegration } from '../connection-points/IntegrationsStep';
import { apiClient } from '../../api';
import { getRuntimeVariablesForCategories } from '../../utils/runtimeVariables';

interface Integration {
  id: string;
  name: string;
  type: string;
  content?: any;
}

interface MpxTenantIntegrationsTabProps {
  integrations: integrationIntegration[];
  onIntegrationsChange: (integrations: integrationIntegration[]) => void;
  onValidationChange?: (hasErrors: boolean) => void;
  onSave?: () => void;
  isModified?: boolean;
  isSaving?: boolean;
}

const MpxTenantIntegrationsTab: React.FC<MpxTenantIntegrationsTabProps> = ({
  integrations,
  onIntegrationsChange,
  onValidationChange,
  onSave,
  isModified = false,
  isSaving = false
}) => {
  const [availableIntegrations, setAvailableIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [runtimeVariables, setRuntimeVariables] = useState<Record<string, { label: string; example: string; description?: string }>>({});
  const [showOverview, setShowOverview] = useState(false);
  const [availableEventTypes, setAvailableEventTypes] = useState<Array<{ value: string; label: string; description: string }>>([]);

  useEffect(() => {
    fetchIntegrations();
    fetchRuntimeVars();
    fetchEventTypes();
  }, []);

  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/integrations');
      setAvailableIntegrations(response.data);
    } catch (error: any) {
      setError(error.message || 'Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  const fetchRuntimeVars = async () => {
    try {
      const vars = await getRuntimeVariablesForCategories(['mpx_tenant']);
      setRuntimeVariables(vars);
    } catch (error: any) {
      console.error('Failed to load runtime variables:', error);
    }
  };

  const fetchEventTypes = async () => {
    try {
      const response = await apiClient.get('/v1/integrations/event-types?category=mpx_tenant');
      setAvailableEventTypes(response.data.event_types || []);
    } catch (error: any) {
      console.error('Failed to load event types:', error);
      // Default event types for mpx_tenant
      setAvailableEventTypes([
        { value: 'created', label: 'Created', description: 'When an MPX tenant is created' },
        { value: 'updated', label: 'Updated', description: 'When an MPX tenant is updated' },
        { value: 'deleted', label: 'Deleted', description: 'When an MPX tenant is deleted' },
      ]);
    }
  };

  const handleIntegrationsChange = (newIntegrations: integrationIntegration[]) => {
    onIntegrationsChange(newIntegrations);
  };

  const handleValidationChange = (hasErrors: boolean) => {
    if (onValidationChange) {
      onValidationChange(hasErrors);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading integrations...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-triangle me-2"></i>
        {error}
      </div>
    );
  }

  return (
    <div className="tab-pane-content">
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <div>
          <h5 className="mb-1">
            <i className="fas fa-plug me-2"></i>
            MPX Tenant Integrations
          </h5>
          <p className="text-muted small mb-0">
            Configure integrations that trigger when MPX tenant events occur
          </p>
        </div>
        <button
          className="btn btn-sm btn-outline-info"
          onClick={() => setShowOverview(!showOverview)}
        >
          <i className={`fas fa-${showOverview ? 'eye-slash' : 'info-circle'} me-1`}></i>
          {showOverview ? 'Hide' : 'Show'} Runtime Variables
        </button>
      </div>

      {showOverview && (
        <div className="alert alert-info mb-4">
          <h6 className="alert-heading">
            <i className="fas fa-info-circle me-2"></i>
            Available Runtime Variables for MPX Tenant
          </h6>
          <div className="small">
            <p className="mb-2">
              These variables are available in integration payloads for MPX tenant events:
            </p>
            <ul className="mb-0">
              {Object.entries(runtimeVariables).map(([key, value]) => (
                <li key={key}>
                  <code>{`{{${key}}}`}</code> - {value.label}
                  {value.example && <> (e.g., <code>{value.example}</code>)</>}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <IntegrationsStep
        integrations={integrations}
        onChange={handleIntegrationsChange}
        availableIntegrations={availableIntegrations}
        onValidationChange={handleValidationChange}
        runtimeVariables={runtimeVariables}
        availableEventTypes={availableEventTypes}
      />

      {isModified && onSave && (
        <div className="mt-4 d-flex justify-content-end">
          <button
            className="btn btn-primary"
            onClick={onSave}
            disabled={isSaving}
          >
            <i className={`fas ${isSaving ? 'fa-spinner fa-spin' : 'fa-save'} me-2`}></i>
            {isSaving ? 'Saving Integrations...' : 'Save Integrations'}
          </button>
        </div>
      )}
    </div>
  );
};

export default MpxTenantIntegrationsTab;
